import { useMemo, useState } from "react";
import { driveFolderUrl } from "../utils/drive.js";

// These three published portrait previews lost their orientation metadata.
// Rotate their presentation only while the delivered pixels remain landscape;
// a future upright replacement therefore will not be rotated a second time.
const SIDEWAYS_PREVIEWS = new Set([
  "1gMPRFO40eXOQ-z5kLe19d0d1BZFe3OgM.jpg",
  "1V39zWkxdGxDX8Stgtfl7g5iUrWmXQQTW.jpg",
  "1E7goVCCMQnJCcWNwQU-xds2b7_4iVU9l.jpg",
]);

function PreviewPhoto({ src, alt }) {
  const [rotate, setRotate] = useState(false);
  if (rotate) return (
    <svg viewBox="0 0 540 720" role="img" aria-label={alt}>
      <image href={src} width="720" height="540" transform="translate(540 0) rotate(90)" />
    </svg>
  );
  return <img src={src} alt={alt} loading="lazy" onLoad={(event) => {
    const img = event.currentTarget;
    if (SIDEWAYS_PREVIEWS.has(src.split("/").pop()) && img.naturalWidth > img.naturalHeight) setRotate(true);
  }} />;
}

const COHORT_LABEL = {
  south: "南班",
  north: "北班",
  all: "全班",
};

const CATEGORY_LABEL = {
  gathering: "迎新／聚會",
  visit: "職業參訪",
  festival: "節慶活動",
  trip: "班級出遊",
};

const FILTERS = [
  ["all", "全部"],
  ["gathering", "迎新／聚會"],
  ["visit", "職業參訪"],
  ["festival", "節慶活動"],
  ["trip", "班級出遊"],
];

function categoryOf(activity) {
  return activity.category || "gathering";
}

export default function UnifiedArchive({
  activities,
  automaticActivitiesLoaded,
  activityPreviewPhotos,
  moments,
  momentsEnabled,
  momentsCluster,
}) {
  const [activeFilter, setActiveFilter] = useState("all");
  const filteredActivities = useMemo(
    () => activeFilter === "all"
      ? activities
      : activities.filter((activity) => categoryOf(activity) === activeFilter),
    [activeFilter, activities],
  );
  const heroVisit = activities.find((activity) => categoryOf(activity) === "visit");
  const hasFestivalActivity = activities.some((activity) => categoryOf(activity) === "festival");
  const showTeacherDay = !hasFestivalActivity && (activeFilter === "all" || activeFilter === "festival");

  function selectCategory(category) {
    setActiveFilter(category);
    document.getElementById("activities")?.scrollIntoView({ behavior: "smooth" });
  }

  return (
    <div className="app archive-app">
      <header className="archive-brandbar">
        <div className="archive-brandmark">
          <img src="/ncku-emba-logo.jpeg" alt="國立成功大學 EMBA" />
          <div className="archive-brandcopy">
            <small>CLASS OF E118</small>
            <b>班級活動</b>
          </div>
        </div>
        <a className="archive-back" href="https://emba.aqualux.dev">← 回班級面板</a>
      </header>

      <div className="archive-issue-head">
        <span>VOL. 01 · 2026</span>
        <b>E118 班級活動</b>
        <span>NCKU EMBA</span>
      </div>

      <main>
        <section className="archive-cover">
          <div className="archive-cover-main">
            <img
              className="archive-cover-photo"
              src="/images/new-student-camp/e118-camp-class-group.jpg"
              alt="E118 新生營第二天，同學與工作團隊在草地上的大合照"
            />
            <div className="archive-cover-title">
              <div className="archive-kicker archive-kicker-gold">E118 · 同窗紀事</div>
              <h1>同窗的<br />日子</h1>
              <p>剛見面時，還得低頭看一下名牌。<br />後來，遠遠看見就會招手了。</p>
            </div>
          </div>

          <div className="archive-cover-side">
            {heroVisit?.coverImage ? (
              <img className="archive-visit-photo" src={heroVisit.coverImage} alt="歐樂沃築夢城堡參訪合照" />
            ) : <div className="archive-visit-photo archive-visit-photo-empty" />}
            <div className="archive-side-copy">
              <small>FIELD STUDY · {heroVisit?.dateLabel || "2026"}</small>
              <h2>到課本以外走走</h2>
              <p>這次把教室搬到歐樂沃築夢城堡。一起聽故事、認識巧克力，也在城堡前留張合照。</p>
              {heroVisit && (
                <a href={driveFolderUrl(heroVisit.driveFolderId)}>
                  開啟參訪相簿　→
                </a>
              )}
            </div>
          </div>
        </section>

        <section className="archive-guide-wrap">
          <div className="archive-guide-head">
            <div>
              <div className="archive-kicker">EXPLORE BY CATEGORY</div>
              <h2>還記得那一天嗎？</h2>
            </div>
            <p>翻到熟悉的臉，也想起那天聊過的話。</p>
          </div>
          <div className="archive-guide">
            <button type="button" onClick={() => selectCategory("gathering")}><b>迎新／聚會</b><span>一起吃飯，慢慢熟起來</span></button>
            <button type="button" onClick={() => selectCategory("visit")}><b>職業參訪</b><span>走出教室，看看不同的現場</span></button>
            <button type="button" onClick={() => selectCategory("festival")}><b>節慶活動</b><span>記得彼此，也記得說聲謝謝</span></button>
            <button type="button" onClick={() => selectCategory("trip")}><b>班級出遊</b><span>找一天，一起去走走</span></button>
          </div>
        </section>

        {!hasFestivalActivity && (
          <section className="archive-upcoming">
            <small>UPCOMING / DRAFT</small>
            <div>
              <h2>教師節活動</h2>
              <p>想說的謝謝，留在這一天。</p>
            </div>
            <span>內容整理中</span>
          </section>
        )}

        <section className="archive-library" id="activities">
          <div className="archive-library-head">
            <div>
              <div className="archive-kicker">ACTIVITY LIBRARY</div>
              <h2>活動回顧</h2>
              <div className="archive-sub">
                {automaticActivitiesLoaded
                  ? `${activities.length} 場相聚${momentsEnabled ? "，還有日常散影" : ""} · 從最近的一天翻起`
                  : "相簿整理中，先看看這些熟悉的片刻。"}
              </div>
            </div>
            <div className="archive-filters" aria-label="活動分類篩選">
              {FILTERS.map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  className={activeFilter === value ? "active" : ""}
                  aria-pressed={activeFilter === value}
                  onClick={() => setActiveFilter(value)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="archive-event-grid">
            {filteredActivities.map((activity, index) => {
              const previews = activityPreviewPhotos[activity.id] || [];
              const photos = previews.length ? previews : activity.coverImage ? [activity.coverImage] : [];
              const category = CATEGORY_LABEL[categoryOf(activity)] || CATEGORY_LABEL.gathering;
              const cohort = COHORT_LABEL[activity.cohortBand];
              return (
                <article
                  key={activity.id}
                  className={`archive-event ${index === 0 ? "is-wide" : ""}`}
                >
                  {photos.length > 0 && (
                    <a className="archive-event-previews" aria-label={`${activity.title}相簿預覽`} href={driveFolderUrl(activity.driveFolderId)}>
                      {photos.map((src, photoIndex) => (
                        <PreviewPhoto key={src} src={src} alt={`${activity.title}預覽照片 ${photoIndex + 1}`} />
                      ))}
                    </a>
                  )}
                  <div className="archive-event-copy">
                    <div className="archive-event-meta">
                      <span>{category}{cohort ? ` · ${cohort}` : ""}</span>
                      <time>{activity.dateLabel}</time>
                    </div>
                    <h3><a href={driveFolderUrl(activity.driveFolderId)}>{activity.title}</a></h3>
                    {activity.location && <p>{activity.location}</p>}
                    <div className="archive-event-cta archive-album-links">
                      <a href={driveFolderUrl(activity.driveFolderId)}>{activity.albumLabel || "開啟活動相簿"}　→</a>
                      {activity.additionalAlbums?.map((album) => (
                        <a key={album.driveFolderId} href={driveFolderUrl(album.driveFolderId)}>{album.label}　→</a>
                      ))}
                    </div>
                  </div>
                </article>
              );
            })}

            {showTeacherDay && (
              <article className="archive-event is-future">
                <div className="archive-event-copy">
                  <div className="archive-event-meta"><span>節慶活動 · 待整理</span><time>日期待確認</time></div>
                  <h3>教師節活動</h3>
                  <p>想說的謝謝，留在這一天。</p>
                  <span className="archive-event-cta">內容整理中</span>
                </div>
              </article>
            )}

            {filteredActivities.length === 0 && !showTeacherDay && (
              <div className="archive-empty">
                <strong>等大家有空，一起去走走</strong>
                <span>下次出遊的照片，就留在這裡。</span>
              </div>
            )}
          </div>

          {momentsEnabled && (
            <a className="archive-moments" href={driveFolderUrl(moments.driveFolderId)} aria-label="開啟散影相簿">
              <div>
                <div className="archive-kicker">MOMENTS</div>
                <h3>散影</h3>
                <p>下課後再聊兩句，散席前再拍一張。<br />沒特別安排，卻也捨不得刪掉的照片。</p>
              </div>
              <div className="archive-photo-strip">
                {momentsCluster.length > 0
                  ? momentsCluster.map((photo) => (
                      <img key={photo.id} src={photo.thumb} alt="E118 班級活動散影" loading="lazy" referrerPolicy="no-referrer" />
                    ))
                  : <span>翻看散影　→</span>}
              </div>
            </a>
          )}
        </section>
      </main>

      <footer className="archive-footer">
        <strong>NCKU EMBA · E118</strong>
        <span>同窗紀事</span>
      </footer>
    </div>
  );
}
